import { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../supabase";

const ContactComponent = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const checkDatabaseRateLimit = async (ip: string) => {
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    const { count, error } = await supabase
      .from("messages")
      .select("*", { count: "exact", head: true })
      .eq("ip_address", ip)
      .gt("created_at", yesterday);

    if (error) {
      console.error("Rate limit check failed:", error);
      return true; // Proceed anyway on error but log it
    }

    if (count !== null && count >= 5) {
      setErrorMessage("Anti-spam limit reached. Please try again tomorrow! 🙏");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget; // Capture form reference immediately!
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      // 1. Get current IP
      const response = await fetch("https://api64.ipify.org?format=json");
      const { ip } = await response.json();

      // 2. Check DB if this IP already sent > 5 messages in 24h
      const canProceed = await checkDatabaseRateLimit(ip);
      if (!canProceed) {
        setIsSubmitting(false);
        return;
      }

      const formData = new FormData(form);
      const newMessage = {
        fullname: formData.get("fullname"),
        email: formData.get("email"),
        message: formData.get("message"),
        ip_address: ip, // Save IP to DB for the limit check
      };

      const { error: insertError } = await supabase
        .from("messages")
        .insert([newMessage]);

      if (insertError) throw insertError;

      setIsSubmitting(false);
      setIsSuccess(true);
      (e.target as HTMLFormElement).reset();
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (error: any) {
      console.error("Submission failed:", error);
      alert(`Error: ${error.message || "Unknown error"}`);
      setIsSubmitting(false);
    }
  };

  return (
    <article className="contact active" data-page="contact">
      <header>
        <h2 className="h2 article-title">{t(`navbar.contact`)}</h2>
      </header>

      <section className="mapbox" data-mapbox>
        <figure>
          <iframe
            src="https://maps.google.com/maps?q=Tunisia,%20Bizerte&t=&z=13&ie=UTF8&iwloc=&output=embed"
            width="400"
            height="300"
            loading="lazy"
          ></iframe>
        </figure>
      </section>

      <section className="contact-form">
        <h3 className="h3 form-title">{t("form.title")}</h3>
        <p className="form-funny-note">{t("form.funnyNote")}</p>

        {errorMessage && (
          <p
            className="form-error-msg"
            style={{
              color: "#ef4444",
              marginBottom: "15px",
              fontSize: "14px",
              fontWeight: "600",
              textAlign: "center",
            }}
          >
            {errorMessage}
          </p>
        )}

        <form onSubmit={handleSubmit} className="form" data-form>
          <div className="input-wrapper">
            <input
              type="text"
              name="fullname"
              className="form-input"
              placeholder={t(`form.fullName`)}
              required
              data-form-input
            />

            <input
              type="email"
              name="email"
              className="form-input"
              placeholder={t(`form.emailAdress`)}
              required
              data-form-input
            />
          </div>

          <textarea
            name="message"
            className="form-input"
            placeholder={t(`form.message`)}
            required
            data-form-input
          ></textarea>

          <button
            className="form-btn"
            type="submit"
            data-form-btn
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span>Sending...</span>
            ) : isSuccess ? (
              <span style={{ color: "#4ade80" }}>✓ Message Sent!</span>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576 6.636 10.07Zm6.787-8.201L1.591 6.602l4.339 2.76 7.494-7.493Z" />
                </svg>
                <span>{t(`form.sendMessage`)}</span>
              </>
            )}
          </button>
        </form>
      </section>
    </article>
  );
};
export default ContactComponent;
